import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "../../../components/ProductCard";
import { ProductCarouselShimmer } from "../../../components/ShimmerEffect";

const BestSellers = ({
  bestSellersLoading,
  filteredBestSellers,
  searchQuery,
  handleAddToCart,
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [filteredBestSellers]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo =
        direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  return (
    <section className="py-14 bg-white dark:bg-gray-950 relative">
      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400">
              <TrendingUp size={20} />
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                Best Sellers
              </h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                Top-selling laptops this week
              </p>
            </div>
          </div>
          <Link
            to="/all-products?sort=best-sellers"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            View All <ArrowRight size={15} />
          </Link>
        </div>

        {/* Cards */}
        {bestSellersLoading ? (
          <ProductCarouselShimmer count={5} />
        ) : filteredBestSellers.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-center py-10 text-sm">
            {searchQuery
              ? `No best sellers found for "${searchQuery}"`
              : "No best sellers available"}
          </p>
        ) : (
          <div className="relative group">
            {/* Left Scroll Button */}
            <button
              onClick={() => scroll("left")}
              className={`absolute left-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${
                canScrollLeft
                  ? "opacity-100 cursor-pointer"
                  : "opacity-0 pointer-events-none"
              }`}
              style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>

            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex overflow-x-auto pb-6 gap-5 snap-x scrollbar-hide scroll-smooth"
            >
              {filteredBestSellers.map((product) => (
                <div
                  key={product._id || product.id}
                  className="w-full sm:w-[280px] lg:w-[290px] snap-center shrink-0"
                >
                  <ProductCard product={product} onAddToCart={handleAddToCart} />
                </div>
              ))}
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={() => scroll("right")}
              className={`absolute right-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${
                canScrollRight
                  ? "opacity-100 cursor-pointer"
                  : "opacity-0 pointer-events-none"
              }`}
              style={{ background: "var(--surface-elevated)", borderColor: "var(--surface-border)" }}
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BestSellers;
